/*
 * Ein Touchpanel unter "Anlagen, Touchpanels": was der Server über das Gerät
 * weiss, Fernzugriff und die Verwaltung über manage.v1. Für Android und
 * sinnux dieselbe Seite; welche Reiter und Knöpfe es gibt, folgt der
 * Freigabeliste des Panels (PanelManager).
 */
import type { ComponentChildren } from 'preact';
import { AppShell } from '../components/AppShell';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { StatusBadge } from '../components/ui/StatusBadge';
import { PanelManager } from '../components/PanelManageModal';
import { RemoteSessionPanel } from '../components/RemoteSessionPanel';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import { sessionSignal } from '../auth/session';
import {
  appLabel,
  modelLabel,
  panelLabel,
  platformLabel,
  type TouchpanelDto,
} from '../components/touchpanelModel';
import { OnlineBadge, Reported } from './TouchpanelsPage';
import '../components/PanelManageModal.css';
import './TouchpanelsPage.css';

interface Props {
  id?: string;
}

interface DetailResponse {
  touchpanel: TouchpanelDto;
}

function Row({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

function formatUptime(seconds: number | null): string | null {
  if (seconds === null) return null;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return d > 0 ? `${d} d ${h} h` : h > 0 ? `${h} h ${m} min` : `${m} min`;
}

export function TouchpanelDetailPage({ id }: Props) {
  const state = useAsync(
    (signal) => api.get<DetailResponse>(`/api/touchpanels/${id}`, signal),
    [id],
  );
  const permissions = sessionSignal.value.user?.permissions ?? [];

  if (state.status === 'loading') {
    return <AppShell title="Touchpanel"><p class="muted">Lade Touchpanel…</p></AppShell>;
  }
  if (state.status === 'error') {
    const status = state.error instanceof ApiError ? state.error.status : 0;
    return (
      <AppShell title="Touchpanel">
        <p class="hv-touchpanels__error">
          {status === 404 ? 'Dieses Touchpanel gibt es im Datenbereich nicht.'
            : status === 403 ? 'Dafür fehlt dir das Recht manage.read.'
              : 'Touchpanel konnte nicht geladen werden.'}
        </p>
        <p><a href="/touchpanels">Zurück zur Liste</a></p>
      </AppShell>
    );
  }

  const p = state.data.touchpanel;
  const canShell = permissions.includes('remote.shell');
  // Ohne Liste der Fähigkeiten (sinnux meldet sie noch nicht) nicht verstecken.
  const hasShell = p.capabilities.length === 0 || p.capabilities.includes('shell.v1');

  return (
    <AppShell title={`Touchpanel · ${panelLabel(p)}`}>
      <div class="hv-touchpanels hv-touchpanel">
        <p class="hv-touchpanel__crumbs">
          <a href="/touchpanels">Touchpanels</a> · <span>{panelLabel(p)}</span> <OnlineBadge p={p} />
        </p>

        <div class="hv-touchpanel__top">
          <Card>
            <CardHeader><h2 class="hv-touchpanel__h">Gerät</h2></CardHeader>
            <CardBody>
              <dl class="hv-touchpanel__facts">
                <Row label="Typ">{p.model ? modelLabel(p.model) : <Reported value={p.chassis} />}</Row>
                <Row label="System">{platformLabel(p.platform)} <span class="muted"><Reported value={p.osPretty} /></span></Row>
                <Row label="Firmware"><Reported mono value={p.firmwareBuild} /></Row>
                <Row label="Kernel"><Reported mono value={p.kernel} /></Row>
                <Row label="Seriennummer"><Reported mono value={p.serial} /></Row>
                <Row label="Chip-Seriennummer"><Reported mono value={p.chipSerial} /></Row>
                {p.nameplateSerial && p.nameplateSerial !== p.serial && (
                  <Row label="Typenschild"><Reported mono value={p.nameplateSerial} /></Row>
                )}
                <Row label={appLabel(p.platform)}><Reported mono value={p.appVersion} /></Row>
                <Row label="NetPilot"><Reported mono value={p.netPilotVersion} /></Row>
                <Row label="Härtung">
                  {p.hardeningEffective || p.hardening
                    ? <>{p.hardeningEffective ?? p.hardening}{p.hardening && p.hardeningEffective && p.hardening !== p.hardeningEffective
                        && <span class="muted"> (eingestellt {p.hardening})</span>}</>
                    : <Reported value={null} />}
                </Row>
              </dl>
            </CardBody>
          </Card>

          <Card>
            <CardHeader><h2 class="hv-touchpanel__h">Verbindung</h2></CardHeader>
            <CardBody>
              <dl class="hv-touchpanel__facts">
                <Row label="Anlage"><a href={`/plants/${p.plantId}`}>{p.plantName ?? p.plantId.slice(0, 8)}</a></Row>
                <Row label="Zuletzt gesehen">{new Date(p.lastSeenAt).toLocaleString('de-AT')}</Row>
                <Row label="Läuft seit"><Reported value={formatUptime(p.uptimeSeconds)} /></Row>
                <Row label="Adressen">
                  {p.addresses.length === 0 ? <Reported value={null} /> : (
                    <span class="hv-touchpanel__list">
                      {p.addresses.map((a) => <span key={a.iface + a.ip} class="mono">{a.iface} {a.ip}</span>)}
                    </span>
                  )}
                </Row>
                <Row label="Fähigkeiten">
                  {p.capabilities.length === 0 ? <Reported value={null} /> : (
                    <span class="hv-touchpanel__list">
                      {p.capabilities.map((c) => <StatusBadge key={c} tone="info">{c}</StatusBadge>)}
                    </span>
                  )}
                </Row>
              </dl>
            </CardBody>
          </Card>

          <Card>
            <CardHeader><h2 class="hv-touchpanel__h">Fernzugriff</h2></CardHeader>
            <CardBody>
              <div class="hv-touchpanel__access">
                <div>
                  <b>Fernshell</b>
                  <p class="muted">Befehle auf dem Panel ausführen, mit Protokoll.</p>
                  {canShell && hasShell
                    ? <a class="hv-btn hv-btn--sm" href={`/plants/${p.plantId}/shell?client=${p.clientId}`}>Fernshell öffnen</a>
                    : <span class="muted">{canShell ? 'Das Panel meldet keine Fernshell.' : 'Dafür fehlt dir das Recht remote.shell.'}</span>}
                </div>
                <div>
                  <b>Fernwartung über Mesh</b>
                  <p class="muted">Tunnel zum Panel und ins Anlagennetz, zeitlich begrenzt.</p>
                  <RemoteSessionPanel plantId={p.plantId} clientId={p.clientId} clientLabel={panelLabel(p)} />
                </div>
                <div>
                  <b>Artefakt ausrollen</b>
                  <p class="muted">Ein freigegebenes Paket gezielt an dieses Panel schicken.</p>
                  <a class="hv-btn hv-btn--sm" href={`/plants/${p.plantId}/updates`}>Updates der Anlage</a>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <Card>
          <CardHeader><h2 class="hv-touchpanel__h">Verwaltung</h2></CardHeader>
          <CardBody>
            <div class="hv-panel-manage hv-touchpanel__manage">
              {p.online
                ? <PanelManager plantId={p.plantId} clientId={p.clientId} platform={p.platform} />
                : <p class="muted">Das Panel ist gerade nicht erreichbar. Einstellungen lassen sich nur ändern, solange es online ist.</p>}
            </div>
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
